"use client";

import type { Office } from "@homeinstead/schemas";
import type { FooterColumnContentProps } from "../../../types/footer-columns";

import React from "react";
import useScreenSize from "../../../hooks/useScreenSize";
import {
  Accordion as AccordionBase,
  AccordionContent,
  AccordionItem,
  AccordionTriggerPlusMinus,
} from "../../Accordion";
import { FooterColumnContent } from "./FooterColumnContent";
import { FooterPlainColumn } from "./FooterPlainColumn";

type FooterAccordionColumnProps = {
  title: string;
  isCollapsibleOnSmallScreens?: boolean;
  items: Omit<FooterColumnContentProps, "placeholders" | "office">[];
  /** Placeholder values for dynamic content */
  placeholders?: FooterColumnContentProps["placeholders"];
  /** Office document for social media links */
  office?: Office;
};

const FooterAccordionColumn = ({
  title,
  items,
  placeholders,
  office,
}: FooterAccordionColumnProps) => {
  const { isSmallScreen } = useScreenSize();

  // to avoid hydration mismatch, don't render anything until we know the screen size;
  if (isSmallScreen === undefined) {
    return null;
  }

  if (isSmallScreen) {
    return (
      <AccordionBase
        className="footer-accordion-column [&+.plain-footer-column]:mt-8"
        collapsible
        type="single"
      >
        <AccordionItem value={title}>
          <AccordionTriggerPlusMinus className="gap-2 text-md">
            {title}
          </AccordionTriggerPlusMinus>
          <AccordionContent>
            <div className="text-sm leading-6 [&_a]:text-sm">
              {items.map((item) => (
                <FooterColumnContent
                  key={item._key}
                  office={office}
                  placeholders={placeholders}
                  {...item}
                />
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      </AccordionBase>
    );
  }

  return (
    <FooterPlainColumn
      items={items}
      office={office}
      placeholders={placeholders}
      title={title}
    />
  );
};

export { FooterAccordionColumn };
